/* src/index.js - v1.2 - Sign Back In When The Server Asks
   CHANGELOG (v1.1 -> v1.2): the server now rejects API calls that carry no valid login token (routes/*
   v2.x+, services/authz.py), answering 401 {"code":"auth_required"}. Two additions so that is invisible
   to normal users: (1) at startup, a browser that still has a saved login ('nsn_user') but NO token - i.e.
   everyone who logged in before tokens existed - is treated as logged out, so they just see the normal
   login screen instead of a half-working app; (2) any API response with code 'auth_required' (expired,
   revoked, suspended) clears the saved login and reloads to the login screen, at most once every 10
   seconds. Other 401s (wrong password on login / delete-account) are untouched.
   CHANGELOG (v1.0 -> v1.1): Send The Login Token With Every API Call
   CHANGELOG (v1.0 -> v1.1): wraps window.fetch so every same-origin /api/ request carries
   "Authorization: Bearer <token>" when the user has a login token (stored by Login.js under
   'nsn_session_token' after POST /api/login, see routes/auth.py v2.9 + services/auth_tokens.py).
   One wrapper instead of editing ~90 call sites. A caller that sets its own Authorization header
   is left alone. Requests to other origins are never touched, and with no token stored (not logged
   in / older login) nothing changes at all. The server only OBSERVES tokens for now (Phase 1);
   enforcement comes later. */
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

(function installAuthFetch() {
  // (1) Saved login from before tokens existed: treat as logged out so the user simply signs in again.
  try {
    if (localStorage.getItem('nsn_user') && !localStorage.getItem('nsn_session_token')) {
      localStorage.removeItem('nsn_user');
    }
  } catch (e) { /* storage unavailable */ }

  const nativeFetch = window.fetch.bind(window);
  let lastForcedSignIn = 0;
  window.fetch = function (input, init) {
    let sentToken = false;
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isApi = url.startsWith('/api/') || url.startsWith(window.location.origin + '/api/');
      const token = isApi ? localStorage.getItem('nsn_session_token') : null;
      if (token) {
        const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input && input.headers) || {});
        if (!headers.has('Authorization')) headers.set('Authorization', 'Bearer ' + token);
        init = { ...(init || {}), headers };
        sentToken = true;
      }
    } catch (e) { /* never block a request because of this wrapper */ }
    const result = nativeFetch(input, init);
    // (2) Server says the login is no longer valid: go back to the login screen.
    return result.then((res) => {
      try {
        if (res.status === 401 && sentToken && Date.now() - lastForcedSignIn > 10000) {
          res.clone().json().then((body) => {
            if (body && body.code === 'auth_required') {
              lastForcedSignIn = Date.now();
              localStorage.removeItem('nsn_user');
              localStorage.removeItem('nsn_session_token');
              window.location.href = '/';
            }
          }).catch(() => {});
        }
      } catch (e) { /* ignore */ }
      return res;
    });
  };
})();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
